import cloneDeep from 'lodash/cloneDeep';
import toInteger from 'lodash/toInteger';
import { base64Kit } from '@/utils/base64-kit';
import {
    createCanvas,
    createForeignObjectImageUrl,
    exportSize,
    fetchVectorText,
    getDrawContainParams,
    getPathname,
    loadImageAsync,
    serializerHtmlToString,
    serializeSvgText,
} from '@/utils/file-kit';
import { isHttpUri } from '@/utils/image-resource-kit';
import { isEmptyEffect } from '@/utils/layer-kit';
import { getLayerOutputDraft } from '@/utils/layer-output-draft-kit';
import type { IImageLayerProps, ILayerProps } from '@/version-types';
import { getImageEffectResultImgSize } from '../plugins/image-effect/runtime/constants';
import { getCompatibleImageEffectPosition } from '../plugins/image-effect/runtime/effect-position';
import ImageEffectAction, {
    getEffectParams,
    type IImageEffectCanvasResult,
} from '../plugins/image-effect/runtime/image-effect-action';
import { drawBorderRadius } from '../utils/layer';

export interface IRenderImageLayerCanvasOptions {
    /** 是否不渲染效果 */
    noEffect?: boolean;
    /** 是否将图层透明度合成进结果 canvas */
    globalAlpha?: boolean;
    /** 是否不绘制圆角 */
    noNeedBorderRadius?: boolean;
    /** globalAlpha=true 时是否应用 layer.opacity */
    useLayerOpacity?: boolean;
    /** 预加载的基础图片 canvas，用于跳过重复加载 */
    baseImage?: HTMLCanvasElement;
    /** 绘制在图片本体下方的特效 canvas */
    effectUnderlayCanvas?: HTMLCanvasElement;
    /** 当前特效缓存标识 */
    effectCode?: string | null;
    /** 更新特效缓存标识 */
    setEffectCode?: (effectCode: string) => void;
    /** 接收与本次输入指纹一致的特效派生产物 */
    onEffectArtifact?: (artifact: IImageEffectRenderArtifact) => void;
    /** 图片对象保留的正式结果，用于恢复被全局缓存淘汰的截图及边界。 */
    effectArtifact?: IImageEffectRenderArtifact;
    /** 接收已完成的预览快照，保存时可以直接序列化，避免重算。 */
    onEffectPreview?: (result: IImageEffectCanvasResult) => void;
    /** 图片加载失败时上报 */
    reportImageError?: (args: { layerId: string; imageUrl: string }) => void;
    /** 渲染缩放，默认 1；屏幕 resource 使用 Retina scale */
    scale?: number;
    /** 特效缓存策略；提交前重算使用 rebuild，临时本体预览使用 preview-rebuild，普通渲染默认 reuse */
    effectCachePolicy?: 'reuse' | 'rebuild' | 'preview-rebuild';
    /** 源图 GL 预览只复用已存在的基础特效派生图，不触发特效重算 */
    usePersistedEffectOnly?: boolean;
}

/** 图片特效提交前重算结果 */
export interface IRebuildImageLayerEffectResult {
    /** 合成特效后的图片画布 */
    canvas: HTMLCanvasElement;
    /** 与候选图层特效输入匹配的截图地址或缓存 code */
    screenshotUrl: string;
    /** 清空截图字段后的候选图层特效指纹 */
    effectCode: string;
    /** GL 计算得到的特效外扩位置 */
    effectPosition?: NonNullable<IImageLayerProps['effect']>['effectPosition'];
}

/** 与图片图层输入指纹绑定的特效渲染产物 */
export interface IImageEffectRenderArtifact {
    /** 清空派生字段后的特效输入指纹 */
    effectCode: string;
    /** 可持久化的截图 code 或地址 */
    screenshotUrl: string;
    /** GL 计算得到的特效外扩位置 */
    effectPosition?: NonNullable<IImageLayerProps['effect']>['effectPosition'];
}

/** 图片特效画布相对图片本体左上角的局部边界 */
export type IImageLayerEffectPosition = NonNullable<IImageLayerProps['effect']>['effectPosition'];

const effectArtifactCache = new Map<string, IImageEffectRenderArtifact>();
const effectArtifactTasks = new Map<string, Promise<IImageEffectRenderArtifact>>();
const EFFECT_ARTIFACT_CACHE_LIMIT = 100;

const setEffectArtifactCache = (effectCode: string, artifact: IImageEffectRenderArtifact) => {
    effectArtifactCache.delete(effectCode);
    effectArtifactCache.set(effectCode, artifact);
    if (effectArtifactCache.size <= EFFECT_ARTIFACT_CACHE_LIMIT) return;

    const oldestEffectCode = effectArtifactCache.keys().next().value;
    if (oldestEffectCode) effectArtifactCache.delete(oldestEffectCode);
};

const generateEffectArtifact = (
    layer: ILayerProps,
    baseCanvas: HTMLCanvasElement,
    effectCode: string,
    effectParams: string,
    replaceCache: boolean,
) => {
    // 同一输入指纹只允许一个 WASM 任务；保存与屏幕 render 并发时直接共享结果。
    const taskKey = effectCode;
    const pendingTask = effectArtifactTasks.get(taskKey);
    if (pendingTask) return pendingTask;

    const task = (async () => {
        // WASM 会回写 effectPosition；只允许它修改渲染快照，不能污染正式图层数据。
        const renderLayer = cloneDeep(layer);
        const effectBase64 = await ImageEffectAction.runEffectAndGetUrl(renderLayer, baseCanvas, true);
        if (!effectBase64) {
            throw new Error(`Image effect result is empty: ${layer.id}`);
        }
        const effectPosition = getCompatibleImageEffectPosition(
            (renderLayer.data as IImageLayerProps).effect?.effectPosition,
            layer.width,
            layer.height,
            (renderLayer.data as IImageLayerProps).effect,
        );
        const screenshotUrl = replaceCache
            ? base64Kit.replaceCode(effectBase64, effectParams)
            : base64Kit.getCode(effectBase64, effectParams);
        const artifact: IImageEffectRenderArtifact = {
            effectCode,
            screenshotUrl,
            effectPosition: cloneDeep(effectPosition),
        };
        setEffectArtifactCache(effectCode, artifact);
        return artifact;
    })();
    effectArtifactTasks.set(taskKey, task);
    const clearTask = () => {
        if (effectArtifactTasks.get(taskKey) === task) effectArtifactTasks.delete(taskKey);
    };
    void task.then(clearTask, clearTask);
    return task;
};

/**
 * 读取与当前图片图层输入完全匹配的已生成特效产物
 *
 * @param layer - 图片图层数据
 * @returns 命中当前输入指纹的产物；尚未渲染时返回 undefined
 */
export const getImageLayerEffectArtifact = (layer: ILayerProps) => {
    const effectCode = base64Kit.md5(getEffectParams(layer));
    const artifact = effectArtifactCache.get(effectCode);
    if (!artifact) return undefined;
    if (!isHttpUri(artifact.screenshotUrl) && !base64Kit.hasCode(artifact.screenshotUrl)) {
        effectArtifactCache.delete(effectCode);
        return undefined;
    }
    const effect = (layer.data as IImageLayerProps).effect;
    const effectPosition = getCompatibleImageEffectPosition(artifact.effectPosition, layer.width, layer.height, effect);
    return { ...cloneDeep(artifact), effectPosition: cloneDeep(effectPosition) };
};

/**
 * 获取图片图层当前可用的特效局部边界
 *
 * @param layer - 图片图层数据
 * @returns 运行时产物或配方中记录的 effectPosition；没有可用边界时返回 undefined
 */
export const getImageLayerEffectPosition = (layer: ILayerProps): IImageLayerEffectPosition | undefined => {
    const position =
        getImageLayerEffectArtifact(layer)?.effectPosition ?? (layer.data as IImageLayerProps).effect?.effectPosition;
    return cloneDeep(
        getCompatibleImageEffectPosition(position, layer.width, layer.height, (layer.data as IImageLayerProps).effect),
    );
};

const normalizeTransform = (layerData: IImageLayerProps) => {
    const {
        imageTransform: { widthRatio, heightRatio, offsetX, offsetY },
    } = layerData;

    return {
        widthRatio: isNaN(widthRatio) || widthRatio === null ? 1 : widthRatio,
        heightRatio: isNaN(heightRatio) || heightRatio === null ? 1 : heightRatio,
        offsetXRatio: isNaN(offsetX) ? 0 : offsetX,
        offsetYRatio: isNaN(offsetY) ? 0 : offsetY,
    };
};

export const loadBaseImage = async (
    layer: ILayerProps,
    reportImageError?: IRenderImageLayerCanvasOptions['reportImageError'],
) => {
    const { url } = layer.data as IImageLayerProps;
    await base64Kit.getRemoteUrlAsync(url);

    try {
        const img = await loadImageAsync(url);
        return createCanvas(img.width, img.height, img).canvas;
    } catch (_e) {
        reportImageError?.({ layerId: layer.id, imageUrl: url });
        return createCanvas(1, 1).canvas;
    }
};

const applyCutoutMask = async (baseImage: HTMLCanvasElement, cutoutMaskURL?: string) => {
    if (!cutoutMaskURL) return null;

    const { canvas: maskCanvas, ctx: maskCtx } = createCanvas(baseImage.width, baseImage.height);
    const cutoutMaskImg = await loadImageAsync(cutoutMaskURL);
    maskCtx.drawImage(baseImage, 0, 0);
    maskCtx.globalCompositeOperation = 'destination-in';
    maskCtx.drawImage(cutoutMaskImg, 0, 0, baseImage.width, baseImage.height);
    return maskCanvas;
};

const applyLayerMask = async (layerCanvas: HTMLCanvasElement, layer: ILayerProps, renderScale = 1) => {
    const { maskURL, minimumMask } = layer.data as IImageLayerProps;
    if (!maskURL) return layerCanvas;

    const resultWidth = toInteger(layer.width * renderScale);
    const resultHeight = toInteger(layer.height * renderScale);
    const { canvas: maskCanvas, ctx: maskCtx } = createCanvas(resultWidth, resultHeight);
    let finalUrl = maskURL;

    if (/\.svg$/.test(getPathname(maskURL) ?? '')) {
        const svgMaskText = await fetchVectorText(maskURL);
        const { width, height } = exportSize(svgMaskText);
        finalUrl = createForeignObjectImageUrl(
            Math.floor(width),
            Math.floor(height),
            serializerHtmlToString(serializeSvgText(svgMaskText, resultWidth, resultHeight)),
            true,
            '',
            resultWidth,
            resultHeight,
        );
    }

    const maskImg = await loadImageAsync(finalUrl);
    if (minimumMask) {
        maskCtx.drawImage(maskImg, 0, 0, resultWidth, resultHeight);
    } else {
        const res = getDrawContainParams(maskImg.width, maskImg.height, resultWidth, resultHeight);
        maskCtx.drawImage(maskImg, res.dx, res.dy, res.dw, res.dh);
    }

    maskCtx.globalCompositeOperation = 'source-in';
    maskCtx.drawImage(layerCanvas, 0, 0);
    return maskCanvas;
};

export const renderImageBaseLayerCanvas = async (layer: ILayerProps, options: IRenderImageLayerCanvasOptions = {}) => {
    const { width, height, opacity } = layer;
    const layerData = layer.data as IImageLayerProps;
    const {
        imageTransform: { flipH, flipV, scale, rotate: imgRotate },
        cutoutMaskURL,
        borderRadius,
    } = layerData;
    const { widthRatio, heightRatio, offsetXRatio, offsetYRatio } = normalizeTransform(layerData);
    const renderScale = options.scale ?? 1;

    const baseImage = options.baseImage ?? (await loadBaseImage(layer, options.reportImageError));
    const cutoutMaskResult = await applyCutoutMask(baseImage, cutoutMaskURL);
    const resultWidth = toInteger(width * renderScale);
    const resultHeight = toInteger(height * renderScale);
    const offsetX = toInteger(offsetXRatio * widthRatio * width * scale * renderScale);
    const offsetY = toInteger(offsetYRatio * heightRatio * height * scale * renderScale);
    const originImgWidth = toInteger(width * widthRatio * renderScale);
    const originImgHeight = toInteger(height * heightRatio * renderScale);
    const imgOffsetX = resultWidth / 2 + (flipH ? -offsetX : offsetX);
    const imgOffsetY = resultHeight / 2 + (flipV ? -offsetY : offsetY);

    const { canvas: layerCanvas, ctx: layerCtx } = createCanvas(resultWidth, resultHeight);
    layerCtx.save();
    layerCtx.translate(imgOffsetX, imgOffsetY);

    let rotate = imgRotate;
    if (flipH) rotate = -rotate;
    if (flipV) rotate = -rotate;

    layerCtx.rotate((rotate * Math.PI) / 180);
    layerCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
    layerCtx.drawImage(
        cutoutMaskResult || baseImage,
        -originImgWidth / 2,
        -originImgHeight / 2,
        originImgWidth,
        originImgHeight,
    );
    layerCtx.restore();

    const maskedCanvas = await applyLayerMask(layerCanvas, layer, renderScale);
    if (borderRadius && !borderRadius.hidden && !options.noNeedBorderRadius) {
        drawBorderRadius(maskedCanvas, borderRadius, 0, 0);
    }

    if (!options.globalAlpha) return maskedCanvas;

    const { canvas: resultCanvas, ctx: resultCtx } = createCanvas(maskedCanvas.width, maskedCanvas.height);
    if (options.useLayerOpacity !== false) {
        resultCtx.globalAlpha = opacity;
    }
    resultCtx.drawImage(maskedCanvas, 0, 0);
    return resultCanvas;
};

const applyLayerOpacity = (canvas: HTMLCanvasElement, layer: ILayerProps, options: IRenderImageLayerCanvasOptions) => {
    if (!options.globalAlpha) return canvas;

    const { canvas: resultCanvas, ctx: resultCtx } = createCanvas(canvas.width, canvas.height);
    if (options.useLayerOpacity !== false) {
        resultCtx.globalAlpha = layer.opacity;
    }
    resultCtx.drawImage(canvas, 0, 0);
    return resultCanvas;
};

const getEffectResource = async (
    layer: ILayerProps,
    targetCanvas: HTMLCanvasElement,
    effectUrl: string,
    effectPosition?: NonNullable<IImageLayerProps['effect']>['effectPosition'],
    renderScale = 1,
) => {
    const { width, height } = targetCanvas;
    const effectImg = await loadImageAsync(effectUrl);
    const imgEffectResultImgSize = getImageEffectResultImgSize(layer.data as IImageLayerProps);
    const resultWidth = effectPosition
        ? Math.max(1, toInteger((effectPosition.right - effectPosition.left) * renderScale))
        : width * imgEffectResultImgSize;
    const resultHeight = effectPosition
        ? Math.max(1, toInteger((effectPosition.bottom - effectPosition.top) * renderScale))
        : height * imgEffectResultImgSize;
    const { canvas: resultCanvas, ctx: resultCtx } = await createCanvas(resultWidth, resultHeight);
    resultCtx.drawImage(
        effectImg,
        0,
        0,
        effectImg.width,
        effectImg.height,
        0,
        0,
        resultCanvas.width,
        resultCanvas.height,
    );
    resultCtx.drawImage(
        targetCanvas,
        effectPosition ? -effectPosition.left * renderScale : (resultCanvas.width - width) / 2,
        effectPosition ? -effectPosition.top * renderScale : (resultCanvas.height - height) / 2,
    );
    return resultCanvas;
};

const drawEffectUnderlayCanvas = (
    layer: ILayerProps,
    targetCanvas: HTMLCanvasElement,
    effectUnderlayCanvas: HTMLCanvasElement,
    renderScale = 1,
    effectPosition = getLayerOutputDraft(layer)?.effectPosition ?? getImageLayerEffectPosition(layer),
) => {
    const { width, height } = targetCanvas;
    const imgEffectResultImgSize = getImageEffectResultImgSize(layer.data as IImageLayerProps);
    const resultWidth = effectPosition
        ? Math.max(1, toInteger((effectPosition.right - effectPosition.left) * renderScale))
        : width * imgEffectResultImgSize;
    const resultHeight = effectPosition
        ? Math.max(1, toInteger((effectPosition.bottom - effectPosition.top) * renderScale))
        : height * imgEffectResultImgSize;
    const { canvas: resultCanvas, ctx: resultCtx } = createCanvas(resultWidth, resultHeight);

    resultCtx.drawImage(
        effectUnderlayCanvas,
        0,
        0,
        effectUnderlayCanvas.width,
        effectUnderlayCanvas.height,
        0,
        0,
        resultCanvas.width,
        resultCanvas.height,
    );
    resultCtx.drawImage(
        targetCanvas,
        effectPosition ? -effectPosition.left * renderScale : (resultCanvas.width - width) / 2,
        effectPosition ? -effectPosition.top * renderScale : (resultCanvas.height - height) / 2,
    );
    return resultCanvas;
};

export const renderImageLayerCanvas = async (layer: ILayerProps, options: IRenderImageLayerCanvasOptions = {}) => {
    const renderScale = options.scale ?? 1;
    const layerEffectIsEmpty = isEmptyEffect(layer.data as IImageLayerProps);

    const layerData = layer.data as IImageLayerProps;
    const baseCanvas = await renderImageBaseLayerCanvas(layer, {
        baseImage: options.baseImage,
        noNeedBorderRadius: options.noNeedBorderRadius,
        reportImageError: options.reportImageError,
        // 透明度只在最终合成阶段应用，避免基础图和特效结果重复乘透明度。
        globalAlpha: false,
        scale: renderScale,
    });

    if (layerEffectIsEmpty) {
        options.setEffectCode?.('');
    }
    if (layerEffectIsEmpty || options.noEffect) {
        return applyLayerOpacity(baseCanvas, layer, options);
    }
    // 特效始终使用 1x 图片本体计算；最终合成时仅缩放特效层，Retina 图片本体保持原生分辨率。
    const effectBaseCanvas =
        renderScale === 1
            ? baseCanvas
            : createCanvas(Math.max(1, toInteger(layer.width)), Math.max(1, toInteger(layer.height)), baseCanvas)
                  .canvas;
    // 图片特效已由 GL 处理完成时，直接把 GL 结果作为下层特效与图片本体合成。
    if (options.effectUnderlayCanvas) {
        const effectCanvas = drawEffectUnderlayCanvas(layer, baseCanvas, options.effectUnderlayCanvas, renderScale);
        return applyLayerOpacity(effectCanvas, layer, options);
    }

    const effectParams = getEffectParams(layer);
    const effectCode = base64Kit.md5(effectParams);
    if (options.effectArtifact?.effectCode === effectCode) {
        setEffectArtifactCache(effectCode, options.effectArtifact);
    }
    // 滤镜可能改变透明度，必须基于新的图片本体重算特效轮廓；这里只消费 Canvas 快照，不生成派生 URL。
    if (options.effectCachePolicy === 'preview-rebuild') {
        const previewResult = await ImageEffectAction.runEffectAndGetCanvas(layer, effectBaseCanvas);
        const effectPosition = getCompatibleImageEffectPosition(
            previewResult.effectPosition,
            layer.width,
            layer.height,
            layerData.effect,
        );
        const previewLayer = cloneDeep(layer);
        const previewEffect = (previewLayer.data as IImageLayerProps).effect;
        if (previewEffect) {
            previewEffect.effectPosition = cloneDeep(effectPosition);
        }
        options.onEffectPreview?.({ canvas: previewResult.canvas, effectPosition: cloneDeep(effectPosition) });
        options.onEffectArtifact?.({
            effectCode,
            screenshotUrl: '',
            effectPosition: cloneDeep(effectPosition),
        });
        const effectCanvas = drawEffectUnderlayCanvas(
            previewLayer,
            baseCanvas,
            previewResult.canvas,
            renderScale,
            effectPosition,
        );
        return applyLayerOpacity(effectCanvas, previewLayer, options);
    }

    const effect = layerData.effect!;
    const screenshotUrl = effect.screenshotUrl || '';
    const shouldRebuildEffect = options.effectCachePolicy === 'rebuild';
    const { borderRadius } = layerData;
    const hasVisibleBorderRadius = Boolean(
        borderRadius &&
        !borderRadius.hidden &&
        (borderRadius.leftTop || borderRadius.rightTop || borderRadius.leftBottom || borderRadius.rightBottom),
    );
    let effectPosition = getImageLayerEffectPosition(layer);

    if (options.usePersistedEffectOnly) {
        const artifact = getImageLayerEffectArtifact(layer);
        const persistedUrl = artifact?.screenshotUrl || screenshotUrl;
        if (!persistedUrl) {
            return applyLayerOpacity(baseCanvas, layer, options);
        }
        const effectCanvas = await getEffectResource(
            layer,
            baseCanvas,
            base64Kit.getLocalUrl(persistedUrl),
            artifact?.effectPosition || effectPosition,
            renderScale,
        );
        return applyLayerOpacity(effectCanvas, layer, options);
    }

    if (!shouldRebuildEffect && screenshotUrl && options.effectCode === null && !hasVisibleBorderRadius) {
        const artifact: IImageEffectRenderArtifact = {
            effectCode,
            screenshotUrl,
            effectPosition: cloneDeep(effectPosition),
        };
        setEffectArtifactCache(effectCode, artifact);
        options.setEffectCode?.(effectCode);
        options.onEffectArtifact?.(artifact);
        base64Kit.setRemoteUrl(effectCode, screenshotUrl);
        const effectCanvas = await getEffectResource(layer, baseCanvas, screenshotUrl, effectPosition, renderScale);
        return applyLayerOpacity(effectCanvas, layer, options);
    }

    if (shouldRebuildEffect || options.effectCode !== effectCode) {
        const hasEffectCode = !shouldRebuildEffect && base64Kit.hasCode(effectCode);
        if (hasEffectCode) {
            options.setEffectCode?.(effectCode);
            const artifact = getImageLayerEffectArtifact(layer) ?? {
                effectCode,
                screenshotUrl: effectCode,
                effectPosition: cloneDeep(effectPosition),
            };
            setEffectArtifactCache(effectCode, artifact);
            effectPosition = cloneDeep(artifact.effectPosition);
            options.onEffectArtifact?.(artifact);
        } else {
            const artifact = await generateEffectArtifact(
                layer,
                effectBaseCanvas,
                effectCode,
                effectParams,
                shouldRebuildEffect,
            );
            options.setEffectCode?.(effectCode);
            effectPosition = cloneDeep(artifact.effectPosition);
            options.onEffectArtifact?.(artifact);
        }
    } else {
        const artifact = getImageLayerEffectArtifact(layer);
        if (artifact) {
            effectPosition = cloneDeep(artifact.effectPosition);
            options.onEffectArtifact?.(artifact);
        }
    }

    const localUrl = base64Kit.getLocalUrl(effectCode);
    const effectCanvas = await getEffectResource(layer, baseCanvas, localUrl, effectPosition, renderScale);
    return applyLayerOpacity(effectCanvas, layer, options);
};

/**
 * 基于候选图片图层强制重算特效
 *
 * 该函数直接使用传入图层离屏生成底图，不读取舞台正式图层，并覆盖相同特效指纹的旧缓存。
 *
 * @param layer - 尚未提交的候选图片图层
 * @returns 合成画布、截图地址和特效指纹
 */
export const rebuildImageLayerEffect = async (layer: ILayerProps): Promise<IRebuildImageLayerEffectResult> => {
    if (isEmptyEffect(layer.data as IImageLayerProps)) {
        throw new Error(`Cannot rebuild empty image effect: ${layer.id}`);
    }

    const effectParams = getEffectParams(layer);
    const effectCode = base64Kit.md5(effectParams);
    let artifact: IImageEffectRenderArtifact | undefined;
    const canvas = await renderImageLayerCanvas(layer, {
        effectCachePolicy: 'rebuild',
        globalAlpha: true,
        useLayerOpacity: false,
        onEffectArtifact: (nextArtifact) => {
            artifact = nextArtifact;
        },
    });
    if (!artifact || artifact.effectCode !== effectCode) {
        throw new Error(`Image effect artifact does not match layer data: ${layer.id}`);
    }

    return { canvas, ...artifact };
};
